"use client";

import {
  BrainIcon,
  GitForkIcon,
  MessageSquareTextIcon,
  WrenchIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { ChainOfThoughtStep } from "@/components/ai-elements/chain-of-thought";
import { Response } from "@/components/ai-elements/response";
import { ActivityReasoning } from "./activity-reasoning";
import type { ActivityDisplayNode } from "./group-activity-items-for-display";
import { ParallelToolBatchDisclosure } from "./parallel-tool-batch";
import type { ActivityToolPresentation } from "./render-model";

function assertNeverActivityNode(node: never): never {
  throw new Error(`Unknown Activity display node: ${JSON.stringify(node)}`);
}

/** 完了した Activity の reasoning・中間説明・tool を時系列で描画する。 */
export function ActivityTimeline({
  batchOpenById,
  focusedBatchId,
  items,
  onBatchOpenChange,
  onBatchFocusRestored,
  onBatchTriggerBlur,
  onBatchTriggerFocus,
  renderTool,
}: {
  batchOpenById?: Readonly<Record<string, boolean>>;
  focusedBatchId?: string | null;
  items: ActivityDisplayNode[];
  onBatchOpenChange?: (batchId: string, open: boolean) => void;
  onBatchFocusRestored?: () => void;
  onBatchTriggerBlur?: (batchId: string) => void;
  onBatchTriggerFocus?: (batchId: string) => void;
  renderTool: (
    partIndex: number,
    presentation: ActivityToolPresentation,
  ) => ReactNode;
}) {
  return (
    <div
      className="space-y-3 [&>div+div]:relative [&>div+div]:before:absolute [&>div+div]:before:-top-1.5 [&>div+div]:before:right-0 [&>div+div]:before:left-6 [&>div+div]:before:h-px [&>div+div]:before:bg-border/50 [&>div:last-child>div:first-child>span:last-child]:hidden"
      data-testid="activity-timeline"
    >
      {items.map((item) => {
        if (item.kind === "parallel-tool-batch") {
          return (
            <ChainOfThoughtStep
              className="gap-2.5 text-[13px] leading-7 [&>div:first-child_svg]:size-3"
              icon={GitForkIcon}
              key={`parallel-tool-batch-${item.batchId}`}
              label={
                <ParallelToolBatchDisclosure
                  batch={item}
                  onFocusRestored={onBatchFocusRestored}
                  onOpenChange={(open) =>
                    onBatchOpenChange?.(item.batchId, open)
                  }
                  onTriggerBlur={() => onBatchTriggerBlur?.(item.batchId)}
                  onTriggerFocus={() => onBatchTriggerFocus?.(item.batchId)}
                  open={
                    batchOpenById
                      ? (batchOpenById[item.batchId] ?? false)
                      : undefined
                  }
                  presentation="history"
                  renderTool={renderTool}
                  restoreFocus={focusedBatchId === item.batchId}
                />
              }
              status="complete"
            />
          );
        }
        if (item.kind === "tool") {
          return (
            <ChainOfThoughtStep
              className="gap-2.5 text-[13px] leading-6 [&>div:first-child_svg]:size-3"
              icon={WrenchIcon}
              key={`tool-${item.partIndex}`}
              label={renderTool(item.partIndex, "history")}
              status="complete"
            />
          );
        }
        if (item.kind === "reasoning-group") {
          return (
            <ChainOfThoughtStep
              className="gap-2.5 text-[13px] leading-5 [&>div:first-child_svg]:size-3"
              icon={BrainIcon}
              key={`reasoning-${item.partIndices.join("-")}`}
              label={
                <ActivityReasoning
                  isStreaming={false}
                  presentation="history"
                  text={item.text}
                />
              }
              status="complete"
            />
          );
        }
        if (item.kind === "interim-text") {
          return (
            <ChainOfThoughtStep
              className="gap-2.5 text-[13px] leading-[1.65] [&>div:first-child_svg]:size-3"
              icon={MessageSquareTextIcon}
              key={`interim-${item.partIndex}`}
              label={
                <Response className="w-full max-w-none text-[13px] leading-[1.65] text-foreground/85 [&_p]:!my-0 [&_ol]:!my-1 [&_ul]:!my-1">
                  {item.part.text}
                </Response>
              }
              status="complete"
            />
          );
        }
        return assertNeverActivityNode(item);
      })}
    </div>
  );
}
